/**
 * ProfileModal Component - By John Bellew - Infinia.ie - last updated 15/07/2025
 * 
 * A comprehensive modal for displaying member profile information with:
 * - Multi-type profile support (council, working group, general members)
 * - Profile image loading and display
 * - Video content integration with fallback handling
 * - Dynamic data merging from multiple sources
 * - Tabbed interface for organized information display
 * - Keyboard navigation (Escape key support)
 * - Loading states and error handling
 * - Responsive design for various screen sizes
 * 
 * @param {Object} profileData - Initial profile data object
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {string} type - Profile type: 'workinggroupmember', 'council', or general
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useEffect, useState, useRef } from 'react';
import './Modal.css';
import './ProfileModal.css';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Parser from 'html-react-parser';
import apiHelper from '../utils/apiHelper';
import { useProfile } from '../contexts/ProfileContext';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * ProfileModal component for displaying comprehensive member profile information
 * with support for multiple profile types and media content
 */
function ProfileModal({ profileData, show, onClose, type = 'workinggroupmember' }) {
    const { profile } = useProfile();
    const [profileImage, setProfileImage] = useState(null);
    const [fullProfile, setFullProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [videoSource, setVideoSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    useEffect(() => {
        if (show && profile?.email) {
            apiHelper.logModalOpen(profile.email, 'ProfileModal', profileData?.displayName || profileData?.name || 'Profile', 'Profile');
        }
    }, [show, profile?.email]);

    useEffect(() => {
        const fetchProfileData = async () => {
            if (!profileData?.id || !show) return;

            try {
                setLoading(true);
                
                if (type === 'council') {
                    // For council members, try to get additional profile data but prioritize council JSON data
                    let profileWithMedia = null;
                    try {
                        profileWithMedia = await apiHelper.getMemberProfileByEmail(profileData.email);
                    } catch (error) {
                        // No additional profile data available
                    }
                    
                    // Merge the council data with any additional profile data, prioritizing council data
                    const mergedProfile = {
                        ...profileData,
                        // Media content from profile system
                        photo: profileWithMedia?.photo || null,
                        videoLink: profileWithMedia?.videoLink || null,
                        bio: profileWithMedia?.bio || null,
                        videoURL: profileWithMedia?.videoURL || null,
                        linkedInProfile: profileWithMedia?.linkedInProfile || null,
                        // Use council JSON data for these fields (don't override)
                        jobTitle: profileData.jobTitle || profileWithMedia?.jobTitle || null,
                        memberCohort: profileData.memberCohort || profileWithMedia?.memberCohort || null,
                        company: profileData.company || profileWithMedia?.company || null,
                        representing: profileData.representing || profileWithMedia?.representing || null,
                        startDate: profileData.startDate || profileWithMedia?.startDate || null,
                        endDate: profileData.endDate || profileWithMedia?.endDate || null,
                    };

                    setFullProfile(mergedProfile);
                    setLoading(false);
                    return;
                }
                
                if (type === 'workinggroupmember') {
                    // Get working group member data
                    const wgProfile = await apiHelper.getWorkingGroupMemberProfile(profileData.id);
                    
                    // Try to get profile data, but don't fail if it doesn't exist
                    let profileWithMedia = null;
                    try {
                        profileWithMedia = await apiHelper.getMemberProfileByEmail(wgProfile.email);
                    } catch (error) {
                        // If profile doesn't exist, just continue with working group data
                    }
                    
                    // Merge the data, using profile media content only if it exists
                    const mergedProfile = {
                        ...wgProfile,
                        photo: profileWithMedia?.photo || null,
                        videoLink: profileWithMedia?.videoLink || null,
                        bio: profileWithMedia?.bio || null,
                        videoURL: profileWithMedia?.videoURL || null
                    };
                    
                    setFullProfile(mergedProfile);
                } else {
                    // Get general profile data
                    const generalProfile = await apiHelper.getMemberProfileByEmail(profileData.email);
                    setFullProfile(generalProfile);
                }
                
                setLoading(false);
            } catch (error) {
                setError('Failed to load profile data');
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [profileData?.id, show, type]);

    useEffect(() => {
        const loadImage = async () => {
            if (!fullProfile?.email) return;

            try {
                // fullProfile.photo is already resolved by fetchProfileData above,
                // so reuse it instead of re-running the slow profile lookup.
                if (!fullProfile.photo) {
                    setProfileImage(null);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${fullProfile.photo}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setProfileImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setProfileImage(imageUrl);
            } catch (error) {
                setProfileImage(null);
            }
        };

        loadImage();

        return () => {
            if (profileImage) {
                URL.revokeObjectURL(profileImage);
            }
        };
    }, [fullProfile?.email, fullProfile?.photo]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !fullProfile?.videoLink) return;
            
            loadingRef.current = true;
            setIsVideoLoading(true);

            try {
                
                const tempVideoUrl = `${fullProfile.videoURL}`;

                if(tempVideoUrl){
                    setVideoSource(tempVideoUrl);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const videoUrl = `${apiUrl}graph/getMediaContent/profile-videos/${fullProfile.videoLink}`;
                
                const response = await fetch(videoUrl, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                
                if (!response.ok) throw new Error('Failed to fetch video');
                
                const blob = await response.blob();
                
                if (currentBlobUrl.current) {
                    URL.revokeObjectURL(currentBlobUrl.current);
                }
                
                const newBlobUrl = URL.createObjectURL(blob);
                currentBlobUrl.current = newBlobUrl;
                setVideoSource(newBlobUrl);
                
            } catch (err) {
                setVideoSource(null);
            } finally {
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        loadVideo();

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [fullProfile?.videoLink]);


    const ProfileImage = () => (
        <div style={{ position: 'relative' }}>
            {profileImage ? (
                <div className="image-container" style={{ position: 'relative' }}>
                    <img
                        src={profileImage}
                        alt={fullProfile.name}
                        style={{
                            width: "220px",
                            height: "220px",
                            objectFit: "cover",
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.add('member-initials');
                            e.target.parentElement.textContent = fullProfile.name.split(' ').map(n => n[0]).join('');
                        }}
                    />
                </div>
            ) : (
                <div className="member-initials">
                    {fullProfile.name.split(' ').map(n => n[0]).join('')}
                </div>
            )}
        </div>
    );

    if (!show) return null;

    if (loading) {
        return (
            <div className="custom-modal">
                <div className="modal-content">
                    <div className="modal-header">
                        <span id="modalTitle">Loading...</span>
                        <span className="modalclose pointer fas fa-times" onClick={onClose}></span>
                    </div>
                    <div className="modal-body">
                        <div className="loading-spinner">Loading profile data...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="custom-modal">
                <div className="modal-content">
                    <div className="modal-header">
                        <span id="modalTitle">Error</span>
                        <span className="modalclose pointer fas fa-times" onClick={onClose}></span>
                    </div>
                    <div className="modal-body">
                        <div className="error-message">{error}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="custom-modal">
            <div className="modal-content">
                <div className="modal-header">
                    <span id="modalTitle">
                        {fullProfile.name}
                    </span>
                    <span className="modalclose pointer fas fa-times" onClick={onClose}></span>
                </div>
                <div className="modal-body">
                    {fullProfile && (
                        <>
                            <div className='w-100' style={{ display: 'flex', gap: '2rem' }}>
                                <div style={{ position: 'relative' }}>
                                    <ProfileImage />
                                </div>
                                <Tabs className='profile-tabs w-100'>
                                    <TabList>
                                        <Tab>
                                            <i className="fa-solid fa-user"></i>
                                            <span className="tab-text">Profile</span>
                                        </Tab>
                                        {type === 'council' && (
                                            <Tab>
                                                <i className="fa-solid fa-users"></i>
                                                <span className="tab-text">Council Profile</span>
                                            </Tab>
                                        )}
                                        <Tab>
                                            <i className="fa-solid fa-book"></i>
                                            <span className="tab-text">Bio</span>
                                        </Tab>
                                        <Tab>
                                            <i className="fa-solid fa-video"></i>
                                            <span className="tab-text">Video</span>
                                        </Tab>
                                    </TabList>

                                    <TabPanel>
                                        <div className="profile-info-list">
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Name</span>
                                                <span className="profile-info-value">{fullProfile.name}</span>
                                            </div>
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Company</span>
                                                <span className="profile-info-value">{fullProfile.company || 'n/a'}</span>
                                            </div>
                                            {(!fullProfile.fieldVisibility || fullProfile.fieldVisibility.jobTitle) && (
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Job Title</span>
                                                    <span className="profile-info-value">{fullProfile.jobTitle || 'n/a'}</span>
                                                </div>
                                            )}
                                            {(!fullProfile.fieldVisibility || fullProfile.fieldVisibility.email) && type !== 'workinggroupmember' && type !== 'council' && (
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Email</span>
                                                    {fullProfile.email ? (
                                                        <a href={`mailto:${fullProfile.email}`} className="profile-info-value email-link">
                                                            <i className="fa-solid fa-envelope"></i> {fullProfile.email}
                                                        </a>
                                                    ) : (
                                                        <span className="profile-info-value">n/a</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Member Cohort</span>
                                                <span className="profile-info-value">{fullProfile.memberCohort || 'n/a'}</span>
                                            </div>
                                            
                                            {(!fullProfile.fieldVisibility || fullProfile.fieldVisibility.linkedIn) && type !== 'workinggroupmember' && (
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">LinkedIn</span>
                                                    {fullProfile.linkedInProfile ? (
                                                        <a href={fullProfile.linkedInProfile} 
                                                           target="_blank" 
                                                           rel="noopener noreferrer" 
                                                           className="profile-info-value linkedin-link">
                                                            <i className="fab fa-linkedin"></i> View Profile
                                                        </a>
                                                    ) : (
                                                        <span className="profile-info-value">n/a</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </TabPanel>
                                    
                                    {type === 'council' && (
                                        <TabPanel>
                                            <div className="profile-info-list">
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Council Role</span>
                                                    <span className="profile-info-value">{fullProfile.role || 'n/a'}</span>
                                                </div>
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Representing</span>
                                                    <span className="profile-info-value">{fullProfile.representing || 'n/a'}</span>
                                                </div>
                                                {/* <div className="profile-info-item">
                                                    <span className="profile-info-label">Status</span>
                                                    <span className="profile-info-value">{fullProfile.status || 'n/a'}</span>
                                                </div> */}
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Term</span>
                                                    <span className="profile-info-value">
                                                        {fullProfile.startDate && fullProfile.endDate 
                                                            ? `${new Date(fullProfile.startDate).getDate().toString().padStart(2, '0')}/${(new Date(fullProfile.startDate).getMonth() + 1).toString().padStart(2, '0')}/${new Date(fullProfile.startDate).getFullYear()} - ${new Date(fullProfile.endDate).getDate().toString().padStart(2, '0')}/${(new Date(fullProfile.endDate).getMonth() + 1).toString().padStart(2, '0')}/${new Date(fullProfile.endDate).getFullYear()}`
                                                            : 'n/a'
                                                        }
                                                    </span>
                                                </div>
                                                {/* <div className="profile-info-item">
                                                    <span className="profile-info-label">Member Of</span>
                                                    <span className="profile-info-value">
                                                        {fullProfile.memberOf 
                                                            ? fullProfile.memberOf.split(', ').map((group, index) => (
                                                                <span key={index} className="member-of-tag">{group}</span>
                                                            ))
                                                            : 'n/a'
                                                        }
                                                    </span>
                                                </div> */}
                                                <div className="profile-info-item">
                                                    <span className="profile-info-label">Company</span>
                                                    <span className="profile-info-value">{fullProfile.company || 'n/a'}</span>
                                                </div>
                                            </div>
                                        </TabPanel>
                                    )}

                                    <TabPanel>
                                        <div className="profile-modal-bio-container">
                                            {fullProfile.bio 
                                                ? Parser(fullProfile.bio.replace(/\n/g, '<br/>'))
                                                : <p>No biography available.</p>
                                            }
                                        </div>
                                    </TabPanel>
                                    
                                    <TabPanel>
                                        <div style={{
                                            width: '100%',
                                            maxHeight: '160px !important',
                                            overflow: 'hidden',
                                            margin: '0 !important',
                                            padding: '0 !important'
                                        }}>
                                            {fullProfile.videoLink && videoSource ? (
                                                <video
                                                    controls
                                                    style={{
                                                        width: '100%',
                                                        height: '160px !important',
                                                        maxHeight: '160px !important',
                                                        margin: '0 !important',
                                                        padding: '0 !important',
                                                        objectFit: 'contain',
                                                        backgroundColor: '#000'
                                                    }}
                                                    src={videoSource}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '160px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '4px'
                                                }}>
                                                    <div style={{ textAlign: 'center' }}>
                                                        <i className="fa-solid fa-video-slash" style={{ fontSize: '24px', color: '#666', marginBottom: '8px' }}></i>
                                                        <p style={{ margin: '0', color: '#666' }}>No video available</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </TabPanel>
                                </Tabs>

                            </div>
                            
                            <div className="d-flex align-items-center justify-content-between">
                                
                                {/* <NavLink 
                                    to={`/Members/MemberProfile/${fullProfile.id}`}
                                    onClick={onClose}
                                    className="btn btn-primary btn-sm"
                                >
                                    <i className="fas fa-external-link-alt me-1"></i>
                                    View Full Profile
                                </NavLink> */}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProfileModal;
